'use client'

import { useContext, useState } from 'react'
import { IdentityContext, PeerAccountContext, SafeImage, useAppAuth } from '~/app/_components'
import { FolioItem } from '~/generated/graphql'
import { useLikeHandler } from '../[id]/[[...pageParams]]/_components'
import { Badge, Box, Flex, Modal, Text } from '@mantine/core'
import { editCaptionModalTitle, responsiveImage } from '../styles.css'
import { HeartIcon } from '@heroicons/react/24/outline'
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid'
import { useDisclosure } from '@mantine/hooks'
import { CaptionModalInner } from '../[id]/[[...pageParams]]/_components/shared/CaptionModalInner'

function LikeButton({
  isLiked,
  onClick,
  isSignedIn,
}: {
  isLiked: boolean
  onClick: () => void
  isSignedIn: boolean
}) {
  const HeartComponent = isLiked ? HeartIconSolid : HeartIcon
  return (
    <HeartComponent
      height={20}
      color="black"
      onClick={onClick}
      style={{ cursor: isSignedIn ? 'pointer' : undefined }}
    />
  )
}

function ImageCaption({
  caption,
  isCurrentUserProfile,
  onEditClick,
}: {
  caption?: string | null
  isCurrentUserProfile: boolean
  onEditClick?: () => void
}) {
  if (!caption?.length) return null

  return (
    <Flex align="center" justify="space-between">
      <Flex gap="5px">
        <HeartIcon height={16} />
        <Text size="sm">{caption}</Text>
      </Flex>
      {isCurrentUserProfile && (
        <Badge color="black" size="lg" radius="md" mt="2px" style={{ cursor: 'pointer' }} onClick={onEditClick}>
          EDIT CAPTION
        </Badge>
      )}
    </Flex>
  )
}

function CaptionModal({
  opened,
  onClose,
  selectedImage,
  setSelectedImage,
}: {
  opened: boolean
  onClose: () => void
  selectedImage: FolioItem | undefined
  setSelectedImage: (image: FolioItem | undefined) => void
}) {
  return (
    <Modal
      opened={opened}
      onClose={() => {
        setSelectedImage(undefined)
        onClose()
      }}
      withCloseButton={false}
      centered
      overlayProps={{ backgroundOpacity: 0.8, blur: 0.9, color: 'black' }}
      title="GIVE YOUR IMAGE A CAPTION"
      classNames={{
        title: editCaptionModalTitle,
      }}
    >
      <CaptionModalInner selectedImage={selectedImage} setSelected={setSelectedImage} closeCaption={onClose} />
    </Modal>
  )
}

function GalleryFolioImage({
  fi,
  i,
}: {
  fi: FolioItem
  i: number
}) {
  const { isSignedIn } = useAppAuth()
  const { account } = useContext(PeerAccountContext)
  const { currentAccount } = useContext(IdentityContext)
  const isCurrentUserProfile = currentAccount?.id === account?.id

  const { solidHeart, handleLikePhotoOnClick, handleUnLikeOnClick } = useLikeHandler({
    hasLiked: fi.hasLiked,
  })

  const [selectedImage, setSelectedImage] = useState<FolioItem | undefined>(undefined)
  const [opened, { open: captionOpen, close: captionClose }] = useDisclosure(false)

  const handleBadgeOnClick = () => {
    setSelectedImage(fi)
    captionOpen()
  }

  return (
    <Box
      style={{
        marginBottom: '16px',
        breakInside: 'avoid',
      }}
    >
      <Box
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '550px',
          margin: '0 auto',
        }}
      >
        <SafeImage
          src={fi.file.url}
          alt={`Image ${i + 1}`}
          width={500}
          height={750}
          className={responsiveImage}
          breakpoints={{ xs: 100, sm: 70, md: 50, lg: 30, xl: 20 }}
        />

        {!isCurrentUserProfile && isSignedIn && (
          <Flex
            style={{
              position: 'absolute',
              bottom: '12px',
              left: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '8px',
              padding: '4px',
            }}
            align="center"
            gap="5px"
          >
            <LikeButton
              isLiked={solidHeart}
              onClick={() => (solidHeart ? handleUnLikeOnClick(fi.id) : handleLikePhotoOnClick(fi.id))}
              isSignedIn={isSignedIn}
            />
            {fi.caption?.length ? (
              <Text size="sm" mr="3px">
                {fi.caption}
              </Text>
            ) : null}
          </Flex>
        )}

        {isCurrentUserProfile && (
          <ImageCaption
            caption={fi.caption}
            isCurrentUserProfile={isCurrentUserProfile}
            onEditClick={handleBadgeOnClick}
          />
        )}
      </Box>

      <CaptionModal
        opened={opened}
        onClose={captionClose}
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
      />
    </Box>
  )
}

export default function FolioDesktop() {
  const { account } = useContext(PeerAccountContext)

  return (
    <>
      <Box
        w="100%"
        pt="90px"
        style={{
          columnCount: 4,
          columnGap: '12px',
          paddingBottom: '0px',
          paddingTop: '16px',
          justifyContent: 'center',
        }}
      >
        {account?.folioItems?.map((fi, i) => (
          <GalleryFolioImage fi={fi} key={fi.id} i={i} />
        ))}
      </Box>
    </>
  )
}
